// Overrides for seller-help articles so the text matches the actual new website.
// The old WordPress export referenced features (kuponi, Mediji, Članki tab, "Poizvedbe",
// "Vodja trgovine" gumb itd.), ki jih na novi spletni strani ni — zato te članke tukaj
// na novo zapisujemo. Vse interne povezave so relativne, vsi opisi ustrezajo dejanskim
// poljem v nadzorni plošči (Pregled, Moja trgovina, Moji izdelki, Zgodovina prodaj,
// Povpraševanja strank, Poslani predračuni).
//
// Struktura: vsak vnos lahko nadomesti title / excerpt / content. Members iz articles.ts
// (slug, section, date, featuredImage) ostanejo nedotaknjeni.

export type ArticleOverride = Partial<{
  title: string;
  excerpt: string;
  content: string;
}>;

import { articleRewritesA } from "./articleRewritesA";
import { articleRewritesB } from "./articleRewritesB";
import { articleRewritesC } from "./articleRewritesC";

export const articleOverrides: Record<string, ArticleOverride> = {
  ...articleRewritesA,
  ...articleRewritesB,
  ...articleRewritesC,
  "registracija-najem-spletne-trgovine": {
    excerpt:
      "Korak za korakom: kako se registrirate kot prodajalec, plačate letno najemnino trgovine in dobite dostop do nadzorne plošče na Unikatni Tržnici.",
    content: `
<p>Na <strong>Unikatni Tržnici</strong> svojo spletno trgovino odprete v dveh korakih: registracija uporabniškega računa in plačilo letne najemnine. Nato dobite dostop do nadzorne plošče, kjer urejate trgovino in dodajate izdelke.</p>

<h2>1. Registracija uporabniškega računa</h2>
<ol>
<li>V zgornji navigaciji kliknite na ikono uporabnika in izberite <a class="decorated-link" href="/registracija">Registracija</a>.</li>
<li>Vnesite e-pošto in geslo ter potrdite ustvarjanje računa.</li>
<li>Na vaš e-naslov pošljemo potrditveno povezavo. Če je ne najdete, preverite mapo <em>Promocije</em> ali <em>Nezaželeno</em>.</li>
</ol>

<h2>2. Odprite trgovino kot prodajalec</h2>
<p>Po prijavi obiščite <a class="decorated-link" href="/postani-prodajalec">Postani prodajalec</a> in vnesite osnovne podatke:</p>
<ul>
<li>ime trgovine,</li>
<li>kratek opis vašega ustvarjanja,</li>
<li>mesto.</li>
</ul>
<p>Po oddaji obrazca je vaša trgovina ustvarjena, vendar še neaktivna — vidite jo samo vi.</p>

<h2>3. Plačilo letne najemnine</h2>
<p>Za aktivacijo trgovine je potrebna <strong>aktivna letna naročnina</strong>. Na strani <a class="decorated-link" href="/placilo-najemnine">Plačilo najemnine</a> izberete obdobje, sistem pa pripravi predračun za bančno nakazilo. Ko nakazilo prejmemo, vam administracija aktivira dostop — običajno v 1–2 delovnih dneh — in po e-pošti dobite potrditev.</p>

<h2>4. Dostop do nadzorne plošče</h2>
<p>Po aktivaciji v meniju <strong>Moj račun</strong> najdete <a class="decorated-link" href="/dashboard">nadzorno ploščo</a> z dvema sklopoma:</p>
<ul>
<li><strong>Kupec</strong> — Pregled, Moja naročila, Seznam želja, Moja povpraševanja, Moji predračuni.</li>
<li><strong>Prodajalec</strong> — Pregled (graf prodaje), <a class="decorated-link" href="/trgovina">Moja trgovina</a>, <a class="decorated-link" href="/moji-izdelki">Moji izdelki</a>, <a class="decorated-link" href="/prodaja">Zgodovina prodaj</a>, Povpraševanja strank, Poslani predračuni.</li>
</ul>

<h2>5. Kaj naredite najprej</h2>
<ol>
<li>V <a class="decorated-link" href="/trgovina">Moja trgovina</a> izpolnite obvezne podatke za izdajo predračunov (naslov, slovenski IBAN <em>SI56 + 15 cifer</em>).</li>
<li>Dodajte opis trgovine, družbena omrežja, pravila dostave in vračila.</li>
<li>V <a class="decorated-link" href="/moji-izdelki">Moji izdelki</a> objavite prve izdelke s kakovostnimi fotografijami in jasnimi opisi.</li>
</ol>
<p>Za podrobnosti o oblikovanju cen preberite <a class="decorated-link" href="/clanek/kako-oblikovati-cene-izdelkov">Kako oblikovati cene izdelkov</a>, za fotografijo pa <a class="decorated-link" href="/clanek/fotografije-izdelkov-za-splet">Fotografije izdelkov za splet</a>.</p>

<h2>Pogosta vprašanja in odgovori</h2>
<p><strong>1. Koliko stane najem spletne trgovine?</strong><br/>Cena je objavljena na strani <a class="decorated-link" href="/placilo-najemnine">Plačilo najemnine</a>. Gre za letno naročnino, ki vključuje gostovanje, vzdrževanje in promocijo tržnice.</p>
<p><strong>2. Kako dolgo traja aktivacija po plačilu?</strong><br/>Običajno 1–2 delovna dneva, odkar nakazilo vidimo na našem računu.</p>
<p><strong>3. Ali lahko izdelke dodajam pred plačilom najemnine?</strong><br/>Ne. Brez aktivne naročnine sistem ne dovoli dodajanja in shranjevanja izdelkov niti urejanja podatkov trgovine.</p>
<p><strong>4. Kaj se zgodi, ko se najemnina izteče?</strong><br/>Trgovina in izdelki ostanejo v sistemu, vendar postanejo kupcem nevidni, dokler ne podaljšate naročnine.</p>
<p><strong>5. Ali so kupci vidni samo prodajalcem?</strong><br/>Podatki o prodaji in povpraševanjih so vidni samo vam v vaši nadzorni plošči.</p>
`,
  },

  "prijava-urejanje-trgovine": {
    title: "Prijava in urejanje podatkov trgovine",
    excerpt:
      "Kako se prijavite v svoj račun, dostopate do nadzorne plošče in v zavihku Moja trgovina uredite vse podatke, ki jih kupci in sistem za predračune potrebujejo.",
    content: `
<p>Po opravljeni <a class="decorated-link" href="/clanek/registracija-najem-spletne-trgovine">registraciji in plačilu najemnine</a> imate dostop do svoje trgovine. V tem članku pokažemo, kje se prijavite, kako pridete v nadzorno ploščo in katera polja morate izpolniti v zavihku <strong>Moja trgovina</strong>.</p>

<h2>Prijava v račun</h2>
<ol>
<li>V zgornji desni navigaciji kliknite ikono uporabnika.</li>
<li>Izberite <a class="decorated-link" href="/prijava">Prijava</a> in vnesite e-pošto ter geslo.</li>
<li>Če ste geslo pozabili, uporabite povezavo <a class="decorated-link" href="/pozabljeno-geslo">Pozabljeno geslo</a> — povezavo za ponastavitev pošljemo na vaš e-naslov.</li>
</ol>

<h2>Nadzorna plošča</h2>
<p>Po prijavi vas spletna stran pelje na <a class="decorated-link" href="/dashboard">nadzorno ploščo</a>. V levem meniju imate v zavihku <em>Prodajalec</em> naslednje sklope:</p>
<ul>
<li><strong>Pregled</strong> — graf prodaje skozi čas (1 mesec, 1 leto, 2 leti, 5 let, od začetka) in status naročnine.</li>
<li><strong>Moja trgovina</strong> — vsi podatki o trgovini in pravila.</li>
<li><strong>Moji izdelki</strong> — dodajanje in urejanje izdelkov.</li>
<li><strong>Zgodovina prodaj</strong> — prejeta naročila in odpošiljanje.</li>
<li><strong>Povpraševanja strank</strong> — sporočila kupcev.</li>
<li><strong>Poslani predračuni</strong> — pregled izdanih predračunov.</li>
</ul>

<h2>Urejanje podatkov v zavihku Moja trgovina</h2>
<p>V <a class="decorated-link" href="/trgovina">Moja trgovina</a> izpolnite naslednje sklope:</p>
<ul>
<li><strong>Ime trgovine</strong> (obvezno) in <strong>opis</strong> trgovine — predstavitev, ki jo vidijo obiskovalci vaše strani.</li>
<li><strong>Naslov za račun</strong> (obvezno): poštna številka, mesto in država. Te podatke uporabimo na vseh predračunih in računih.</li>
<li><strong>Bančni podatki</strong> (obvezno): TRR v obliki slovenskega <strong>IBAN-a (SI56 + 15 cifer)</strong> in po želji ime banke. Brez veljavnega IBAN-a sistem ne dovoli izdaje predračunov.</li>
<li><strong>Družbena omrežja</strong> — povezave do Facebooka, Instagrama, YouTuba, Pinteresta in drugih kanalov. Več v članku <a class="decorated-link" href="/clanek/kako-povezati-trgovino-s-socialnimi-omrezji">Kako povezati trgovino s socialnimi omrežji</a>.</li>
<li><strong>Dostava</strong> — opis vaše dostavne politike.</li>
<li><strong>Pravila &amp; sporočila</strong> — <em>Sporočilo kupcem</em>, <em>Pravila dostave</em> in <em>Pravila vračila</em>. Ta besedila se prikažejo kupcu pred zaključkom nakupa in v potrditvenih e-pošta.</li>
</ul>
<p>Spremembe shranite z gumbom na dnu. Če naročnina ni aktivna ali manjkajo obvezni podatki, vas sistem na to opozori.</p>

<h2>Predogled trgovine</h2>
<p>Po shranitvi si lahko kadar koli ogledate, kako vašo trgovino vidijo kupci, prek povezave <em>Odpri trgovino</em>. Tu so vidne vaše osnovne informacije, družbena omrežja in vsi <strong>objavljeni</strong> izdelki.</p>

<h2>Pogosta vprašanja in odgovori</h2>
<p><strong>1. Kje se prijavim v svoj račun?</strong><br/>V zgornji navigaciji kliknite ikono uporabnika in izberite <em>Prijava</em>, ali pa neposredno odprite <a class="decorated-link" href="/prijava">/prijava</a>.</p>
<p><strong>2. Kaj naredim, če pozabim geslo?</strong><br/>Kliknite <em>Pozabljeno geslo</em>; na vaš e-naslov bomo poslali povezavo za ponastavitev.</p>
<p><strong>3. Zakaj sistem ne shrani sprememb v Moji trgovini?</strong><br/>Najpogostejša razloga sta neaktivna naročnina ali neizpolnjeni obvezni podatki (ime trgovine, poštna številka, mesto, IBAN). Manjkajoča polja so označena rdeče.</p>
<p><strong>4. Ali lahko ime trgovine kasneje spremenim?</strong><br/>Da, kadar koli ga uredite v zavihku <a class="decorated-link" href="/trgovina">Moja trgovina</a>.</p>
<p><strong>5. Kje urejam pravila vračila in dostave?</strong><br/>V <em>Moja trgovina → Pravila &amp; sporočila</em>. Besedilo se prikaže na strani trgovine in v potrditvenih e-pošta kupcem.</p>
`,
  },

  "kaj-vse-lahko-urejam-v-nadzorni-plosci": {
    title: "Kaj lahko urejam v nadzorni plošči",
    excerpt:
      "Pregled vseh sklopov, ki jih kot prodajalec urejate v nadzorni plošči Unikatne Tržnice — od podatkov trgovine do izdelkov, predračunov in povpraševanj.",
    content: `
<p>V <a class="decorated-link" href="/dashboard">nadzorni plošči</a> imate kot prodajalec na enem mestu vse, kar potrebujete za vodenje trgovine: nastavitve trgovine, izdelke, naročila, predračune in komunikacijo s kupci.</p>

<h2>Pregled in graf prodaje</h2>
<p>Zavihek <strong>Pregled</strong> prikaže status vaše letne naročnine in <strong>graf prodaje skozi čas</strong>. Časovno obdobje preklapljate med <em>1 mesec</em>, <em>1 leto</em>, <em>2 leti</em>, <em>5 let</em> in <em>od začetka</em>. Iz grafa vidite sezonska nihanja in učinek vaših promocij.</p>

<h2>Moja trgovina</h2>
<p>V <a class="decorated-link" href="/trgovina">Moja trgovina</a> urejate vse, kar predstavlja vaš profil in omogoča izdajo predračunov:</p>
<ul>
<li>ime trgovine in opis,</li>
<li>naslov za račun (poštna št., mesto, država),</li>
<li>bančni podatki — slovenski <strong>IBAN (SI56 + 15 cifer)</strong>,</li>
<li>povezave do družbenih omrežij,</li>
<li>opis dostave,</li>
<li>sporočilo kupcem, pravila dostave in pravila vračila.</li>
</ul>
<p>Več v članku <a class="decorated-link" href="/clanek/prijava-urejanje-trgovine">Prijava in urejanje podatkov trgovine</a>.</p>

<h2>Moji izdelki</h2>
<p>V <a class="decorated-link" href="/moji-izdelki">Moji izdelki</a> dodajate nove izdelke in urejate obstoječe — naslov, ceno, akcijsko ceno, kratek in dolgi opis, kategorije (do 10), zalogo, status (Objavljen / Osnutek / V pregledu / Zasebno), glavno sliko in galerijo. Podrobna navodila so v članku <a class="decorated-link" href="/clanek/dodajanje-izdelkov">Dodajanje in urejanje izdelkov</a>.</p>

<h2>Zgodovina prodaj</h2>
<p>V <a class="decorated-link" href="/prodaja">Zgodovina prodaj</a> najdete vsa prejeta naročila. Za vsako naročilo vidite kupca, izdelke, opombo kupca in povezavo do PDF-predračuna oziroma računa. Ko izdelek odpošljete, vnesete <strong>prevoznika</strong> in <strong>št. sledenja</strong> ter potrdite odpošiljanje — kupec hkrati prejme obvestilo.</p>

<h2>Povpraševanja strank</h2>
<p>Vsak izdelek ima gumb <em>Pošlji vprašanje prodajalcu</em>. Vsa povpraševanja so zbrana v zavihku <strong>Povpraševanja strank</strong>, kjer vidite izdelek, kupca, vprašanje in zgodovino pogovora.</p>

<h2>Predračuni</h2>
<p>V sklopu <strong>Poslani predračuni</strong> imate pregled vseh predračunov, ki ste jih izdali. Novega predračuna ne pripravljate ročno v Wordu — sistem ga ustvari za vas iz polj v zavihku <a class="decorated-link" href="/trgovina">Moja trgovina</a> in iz izbranih izdelkov. Več v <a class="decorated-link" href="/clanek/kako-poslati-predracun-kupcu">Kako pripraviti in poslati predračun kupcu</a>.</p>

<h2>Stvari, ki jih v nadzorni plošči ni</h2>
<p>Da ne iščete po nepotrebnem: na Unikatni Tržnici trenutno <strong>ne ponujamo</strong> samostojnega blog/članki sistema za posamezne trgovine, kuponov za popuste in ločenega zavihka Mediji. Predstavitev in zgodbo zato vključite v <em>Opis trgovine</em> in v <em>Opise izdelkov</em>, vsebine pa lahko delite tudi prek <a class="decorated-link" href="/clanek/kako-povezati-trgovino-s-socialnimi-omrezji">socialnih omrežij</a>.</p>

<h2>Pogosta vprašanja in odgovori</h2>
<p><strong>1. Kje vidim, koliko sem prodal/a?</strong><br/>V zavihku <em>Pregled</em> imate graf prodaje skozi čas; v <a class="decorated-link" href="/prodaja">Zgodovina prodaj</a> pa seznam vseh naročil s podrobnostmi.</p>
<p><strong>2. Ali lahko ustvarim kupon za popust?</strong><br/>Ne, na Unikatni Tržnici sistema kuponov trenutno ni. Akcijsko ceno določite neposredno pri izdelku v zavihku <a class="decorated-link" href="/moji-izdelki">Moji izdelki</a>.</p>
<p><strong>3. Kje pišem blog objave o svojih izdelkih?</strong><br/>Samostojnega blog modula za posamezne trgovine ni. Daljše predstavitve vključite v opis izdelka in opis trgovine, vsebine pa lahko delite na socialnih omrežjih.</p>
<p><strong>4. Kako vidim povpraševanja strank?</strong><br/>V nadzorni plošči odprite <em>Povpraševanja strank</em>; vsako sporočilo vsebuje povezavo do izdelka, na katerega se nanaša.</p>
<p><strong>5. Ali lahko izvozim seznam naročil?</strong><br/>Vsako naročilo ima PDF predračuna in/ali računa, ki ga lahko prenesete iz <a class="decorated-link" href="/prodaja">Zgodovina prodaj</a>.</p>
`,
  },

  "kaj-vse-lahko-spremljam-v-nadzorni-plosci": {
    excerpt:
      "Pregled vseh podatkov, ki jih kot prodajalec spremljate v nadzorni plošči Unikatne Tržnice — graf prodaje, prejeta naročila, povpraševanja kupcev in poslani predračuni.",
    content: `
<p>Nadzorna plošča vam omogoča pregled nad dogajanjem v vaši trgovini — od grafa prodaje do posameznih sporočil kupcev. V tem članku pokažemo, kaj točno vidite in kje to najdete.</p>

<h2>Graf prodaje skozi čas</h2>
<p>V zavihku <a class="decorated-link" href="/dashboard">Pregled</a> imate <strong>graf prodaje</strong> z izbiro obdobja:</p>
<ul>
<li><em>1 mesec</em> — dnevni prikaz prodaje za zadnjih 30 dni,</li>
<li><em>1 leto</em>, <em>2 leti</em>, <em>5 let</em> — mesečna agregacija,</li>
<li><em>od začetka</em> — celotna zgodovina vaše trgovine.</li>
</ul>
<p>Iz grafa hitro razberete sezonska nihanja, učinek akcij in dolgoročno rast.</p>

<h2>Prejeta naročila</h2>
<p>V <a class="decorated-link" href="/prodaja">Zgodovina prodaj</a> so zbrana vsa prejeta naročila. Za vsako vidite:</p>
<ul>
<li>kupca in njegov naslov,</li>
<li>naročene izdelke s količinami in cenami,</li>
<li>opombo kupca,</li>
<li>povezavo do PDF predračuna in računa,</li>
<li>status pošiljke (čaka na odpošiljanje / odposlano s sledilno številko).</li>
</ul>
<p>Ko izdelek odpošljete, vnesete <strong>prevoznika</strong> in <strong>številko sledenja</strong> ter potrdite odpošiljanje — kupec o tem samodejno prejme e-pošto.</p>

<h2>Povpraševanja strank</h2>
<p>Kupci lahko pri vsakem izdelku kliknejo na gumb <em>Pošlji vprašanje prodajalcu</em>. Vsa sporočila so zbrana v zavihku <strong>Povpraševanja strank</strong>, kjer spremljate:</p>
<ul>
<li>na kateri izdelek se vprašanje nanaša,</li>
<li>besedilo kupčevega sporočila,</li>
<li>zgodovino pogovora in vaše odgovore,</li>
<li>datum prejema.</li>
</ul>

<h2>Poslani predračuni</h2>
<p>V sklopu <strong>Poslani predračuni</strong> imate seznam vseh predračunov, ki jih je sistem ustvaril za vašo trgovino, s statusom (čaka na plačilo, plačan, preklican) in povezavo do PDF-ja.</p>

<h2>Status naročnine</h2>
<p>Na vrhu zavihka <em>Pregled</em> je viden status vaše letne naročnine in datum izteka. Ko se obdobje izteka, vas sistem opozori in vodi na <a class="decorated-link" href="/placilo-najemnine">Plačilo najemnine</a>.</p>

<h2>Pogosta vprašanja in odgovori</h2>
<p><strong>1. Ali lahko spremljam prodajo za posamezen izdelek?</strong><br/>Posamezna naročila, v katerih je izdelek bil prodan, vidite v <a class="decorated-link" href="/prodaja">Zgodovina prodaj</a>. Skupni graf v <em>Pregledu</em> prikazuje vsote po obdobjih.</p>
<p><strong>2. Kako hitro vidim novo naročilo?</strong><br/>Naročilo se v <em>Zgodovini prodaj</em> pojavi takoj, ko kupec zaključi nakup; istočasno prejmete tudi e-poštno obvestilo.</p>
<p><strong>3. Ali kupci vidijo moja prodajna poročila?</strong><br/>Ne. Graf prodaje in seznam naročil sta vidna samo vam v vaši nadzorni plošči.</p>
<p><strong>4. Kje vidim, ali je kupec plačal predračun?</strong><br/>Status spremljate v zavihku <em>Poslani predračuni</em>; po prejemu plačila se naročilo v <em>Zgodovini prodaj</em> samodejno označi kot plačano.</p>
<p><strong>5. Kako odgovorim kupcu na povpraševanje?</strong><br/>V zavihku <em>Povpraševanja strank</em> odprete pogovor in vnesete odgovor. Kupec dobi obvestilo na svoj e-naslov in lahko odgovori naprej.</p>
`,
  },

  "dodajanje-izdelkov": {
    excerpt:
      "Navodila za dodajanje in urejanje izdelkov v nadzorni plošči Unikatne Tržnice — slike, opis, cena, kategorije, zaloga in status objave.",
    content: `
<p>Izdelke v vaši trgovini upravljate v zavihku <a class="decorated-link" href="/moji-izdelki">Moji izdelki</a>. Za dodajanje izdelkov mora biti naročnina aktivna in podatki trgovine (naslov, IBAN) izpolnjeni.</p>

<h2>Dodajanje novega izdelka</h2>
<ol>
<li>V nadzorni plošči odprite <a class="decorated-link" href="/moji-izdelki">Moji izdelki</a> in kliknite <strong>Dodaj nov izdelek</strong>.</li>
<li>Naložite <strong>glavno sliko</strong> in po želji <strong>dodatne slike v galerijo</strong>.</li>
<li>Izpolnite osnovna polja izdelka in shranite.</li>
</ol>

<h2>Polja, ki jih vnesete</h2>
<ul>
<li><strong>Naslov</strong> (obvezno) — jasno, opisno ime izdelka. Nasveti v članku <a class="decorated-link" href="/clanek/kako-izbrati-naslov-izdelka">Kako izbrati ustrezen naslov izdelka</a>.</li>
<li><strong>Cena</strong> in po želji <strong>akcijska cena</strong> (v EUR). Akcijska cena se prikaže kot prečrtana redna cena.</li>
<li><strong>Kratek opis</strong> — povzetek, ki se prikaže ob izdelku.</li>
<li><strong>Opis</strong> — daljše besedilo z materiali, načinom izdelave, navodili za nego in posebnostmi. Več v članku <a class="decorated-link" href="/clanek/opis-izdelka-seo">Kaj mora vsebovati dober opis izdelka</a>.</li>
<li><strong>Kategorije</strong> — izberete lahko do 10 kategorij. Pomagajo, da vas kupci najdejo. Priporočila: <a class="decorated-link" href="/clanek/pravilno-oznacevanje-kategorij-in-oznak">Pravilno označevanje kategorij in oznak</a>.</li>
<li><strong>Zaloga</strong> in stikalo <strong>Na zalogi</strong> — če zalogo izklopite, izdelek ostane viden, vendar ga kupec ne more dodati v košarico.</li>
<li><strong>Status</strong> — <em>Objavljen</em> (viden vsem), <em>Osnutek</em> (samo vi), <em>V pregledu</em>, <em>Zasebno</em>.</li>
</ul>

<h2>Slike izdelka</h2>
<p>Glavna slika je prva, ki jo kupec vidi v seznamih in v predogledu trgovine. Galerija dodatnih slik se odpre na strani izdelka. Kakovostne, dobro osvetljene fotografije pomembno povečajo zaupanje in prodajo — priporočila imamo v članku <a class="decorated-link" href="/clanek/fotografije-izdelkov-za-splet">Kako pripraviti fotografije izdelkov za splet</a>.</p>

<h2>Urejanje obstoječega izdelka</h2>
<p>V seznamu izdelkov kliknite <strong>Uredi</strong> pri želenem izdelku, spremenite polja in shranite. Izdelek lahko tudi <strong>izbrišete</strong> — postopek je dokončen, zato za začasno odstranitev raje uporabite status <em>Osnutek</em> ali izklopite <em>Na zalogi</em>.</p>

<h2>Kako kupec vidi izdelek</h2>
<p>Na strani izdelka so prikazani:</p>
<ul>
<li>glavna slika in galerija,</li>
<li>naslov in cena (oz. akcijska cena, če je določena),</li>
<li>kratek in dolgi opis,</li>
<li>gumb <em>Dodaj v košarico</em>,</li>
<li>gumb <em>Pošlji vprašanje prodajalcu</em> (povpraševanje pride v vaš zavihek <em>Povpraševanja strank</em>),</li>
<li>kategorije izdelka in povezava do vaše trgovine.</li>
</ul>

<h2>Pogosta vprašanja in odgovori</h2>
<p><strong>1. Zakaj ne morem shraniti izdelka?</strong><br/>Najpogostejši razlog je neaktivna naročnina ali manjkajoči obvezni podatki trgovine (naslov in IBAN). Sistem vas opozori, kaj manjka.</p>
<p><strong>2. Kako začasno skrijem izdelek iz trgovine?</strong><br/>Pri urejanju spremenite status iz <em>Objavljen</em> v <em>Osnutek</em>, ali pa izklopite <em>Na zalogi</em>.</p>
<p><strong>3. Koliko slik lahko naložim?</strong><br/>Eno glavno sliko in več dodatnih slik v galerijo. Priporočamo vsaj 3–5 fotografij z različnih zornih kotov.</p>
<p><strong>4. Kako določim akcijsko ceno?</strong><br/>V polje <em>Akcijska cena</em> vnesite znižani znesek. Kupcu se prikaže prečrtana redna cena in nova, znižana cena.</p>
<p><strong>5. Ali lahko v izdelek vključim več kategorij?</strong><br/>Da, na izdelek lahko vežete do 10 kategorij. Izberite tiste, kjer kupec dejansko pričakuje vaš izdelek.</p>
<p><strong>6. Kje določim dobavni rok za izdelek po naročilu?</strong><br/>Informacijo vključite v polje <em>Kratek opis</em> ali <em>Opis</em> (npr. „Izdelek izdelam in odpošljem v 7–10 dneh.”).</p>
`,
  },

  "kako-oblikovati-cene-izdelkov": {
    excerpt:
      "Praktičen vodič za oblikovanje cen ročno izdelanih izdelkov na Unikatni Tržnici — material, čas, marža, akcijska cena in vključitev poštnine.",
    content: `
<p>Oblikovanje cene je za marsikaterega prodajalca najtežji del prodaje. Cena mora pokriti material, vaš čas in pošten zaslužek — ob tem pa ostati privlačna za kupca. Na Unikatni Tržnici se ceno določi neposredno pri vsakem izdelku v zavihku <a class="decorated-link" href="/moji-izdelki">Moji izdelki</a>.</p>

<h2>1. Izračunajte realni strošek izdelka</h2>
<ul>
<li><strong>Material:</strong> vse surovine, embalaža in dodatki.</li>
<li><strong>Čas dela:</strong> izberite urno postavko (priporočamo vsaj 10 €/uro) in jo pomnožite s časom izdelave.</li>
<li><strong>Režijski stroški:</strong> elektrika, orodja, amortizacija opreme.</li>
</ul>

<h2>2. Vključite stroške pošiljanja v ceno</h2>
<p>Priporočamo, da stroške <strong>pošiljanja vključite že v ceno izdelka</strong>. Kupci pogosto naročajo pri več prodajalcih hkrati, večkratno ločeno plačilo poštnine pa je neprijetna izkušnja. Vaša pravila in stroške dostave opišete v zavihku <a class="decorated-link" href="/trgovina">Moja trgovina → Dostava</a>.</p>

<h2>3. Dodajte maržo</h2>
<p>Na seštevek materiala, časa in poštnine prištejte <strong>30–50 % marže</strong>. To je vaš pošten zaslužek za znanje, kreativnost in vodenje trgovine.</p>

<h2>4. Preverite konkurenco</h2>
<p>Poglejte podobne izdelke drugih prodajalcev na <a class="decorated-link" href="/kategorije">Unikatni Tržnici</a>. Vaša cena naj bo v razumnem rangu — prenizka cena razvrednoti vaše delo, previsoka odvrne kupce.</p>

<h2>5. Zaokrožite psihološko</h2>
<p>19,90 € deluje bolje kot 20 €. 24,50 € bolje kot 25 €. Drobna razlika, velik učinek.</p>

<h2>Akcijska cena namesto kuponov</h2>
<p>Na Unikatni Tržnici sistema kuponov nimamo. Če želite ponuditi popust, pri izdelku določite <strong>akcijsko ceno</strong> — kupcu se prikaže prečrtana redna in nova, znižana cena. To je preprosto, transparentno in učinkovito.</p>

<h2>Hiter primer</h2>
<p>Ročno izdelana zapestnica:</p>
<ul>
<li>Material: 3 €</li>
<li>Čas (45 min × 10 €/h): 7,50 €</li>
<li>Embalaža in poštnina: 3,50 €</li>
<li>Vmesni seštevek: 14 €</li>
<li>Marža 40 %: 5,60 €</li>
<li><strong>Končna cena: 19,90 €</strong></li>
</ul>

<h2>Pogosta vprašanja in odgovori</h2>
<p><strong>1. Ali kupec poštnino plača posebej?</strong><br/>Priporočamo, da poštnino vključite v ceno izdelka. Vaše konkretno pravilo opišete v <a class="decorated-link" href="/trgovina">Moja trgovina → Dostava</a>.</p>
<p><strong>2. Kako naredim popust brez kupona?</strong><br/>Pri izdelku v <a class="decorated-link" href="/moji-izdelki">Moji izdelki</a> vnesite <em>Akcijsko ceno</em>. Sistem kuponov na tržnici ni na voljo.</p>
<p><strong>3. Kakšno urno postavko naj uporabim?</strong><br/>Za začetek vsaj 10 €/uro. Z izkušnjami, prepoznavnostjo in zahtevnostjo izdelka jo postopno dvigujte.</p>
<p><strong>4. Kako pogosto naj posodabljam cene?</strong><br/>Vsaj enkrat letno in vsakič, ko se znatno spremeni cena materiala ali poštnine.</p>
<p><strong>5. Ali na ceni izdelka piše DDV?</strong><br/>Če ste davčni zavezanec, prikažete znesek z DDV; sicer ne. Davčni status uredite sami z izdajo računov po prejemu plačila — predračune sistem pripravi avtomatsko.</p>
`,
  },

  "kako-poslati-predracun-kupcu": {
    title: "Kako pripraviti in poslati predračun kupcu",
    excerpt:
      "Na Unikatni Tržnici predračun pripravi sistem samodejno iz naročila. V članku pojasnimo, kateri podatki morajo biti izpolnjeni, kako predračun pošljete kupcu in kaj naredite po prejemu plačila.",
    content: `
<p>Na Unikatni Tržnici vam <strong>predračuna ni treba pripravljati ročno v Wordu</strong> — sistem ga ustvari samodejno iz podatkov vaše trgovine in iz naročila, ki ga je oddal kupec. V tem članku pokažemo, kaj morate imeti izpolnjeno in kako ga kupcu pošljete.</p>

<h2>Predpogoji</h2>
<p>Da sistem lahko izda predračun, mora biti v <a class="decorated-link" href="/trgovina">Moja trgovina</a> izpolnjeno:</p>
<ul>
<li>ime trgovine,</li>
<li>naslov za račun (poštna št., mesto, država),</li>
<li>slovenski <strong>IBAN (SI56 + 15 cifer)</strong>.</li>
</ul>
<p>Brez teh podatkov sistem ne dovoli izdaje predračuna in vas v <em>Moja trgovina</em> opozori, katera polja manjkajo.</p>

<h2>Kako nastane predračun</h2>
<ol>
<li>Kupec v košarici zaključi naročilo z načinom plačila po predračunu.</li>
<li>Sistem samodejno pripravi PDF predračun z vsemi obveznimi podatki: številka in datum, podatki vaše trgovine, podatki kupca, izdelki s količinami in cenami, skupni znesek, vaš IBAN in sklic.</li>
<li>Kupec PDF prejme po e-pošti, vi pa ga vidite v zavihku <strong>Poslani predračuni</strong>.</li>
</ol>

<h2>Spremljanje statusa</h2>
<p>V zavihku <em>Poslani predračuni</em> vidite, ali je predračun <strong>čaka na plačilo</strong>, <strong>plačan</strong> ali <strong>preklican</strong>. Hkrati vsako naročilo najdete tudi v <a class="decorated-link" href="/prodaja">Zgodovina prodaj</a> s povezavo do PDF-ja.</p>

<h2>Po prejemu plačila</h2>
<ol>
<li>Preverite priliv na vašem TRR (običajno v 1–2 delovnih dneh).</li>
<li>Ko sistem zazna plačilo, se status naročila samodejno spremeni. Lahko pa kupcu pošljete kratko sporočilo: „Plačilo prejeto, paket odpošiljam jutri.”</li>
<li>Izdelek skrbno zapakirajte in odpošljite — nasveti v <a class="decorated-link" href="/clanek/pakiranje-in-posiljanje-izdelkov">Pakiranje in pošiljanje izdelkov</a>.</li>
<li>V <a class="decorated-link" href="/prodaja">Zgodovina prodaj</a> vnesite <strong>prevoznika</strong> in <strong>številko sledenja</strong>, nato potrdite odpošiljanje. Kupec o tem dobi e-pošto.</li>
</ol>

<h2>Račun po prejetem plačilu</h2>
<p>Če ste davčni zavezanec, po prejemu plačila izdate še račun. PDF računa je v Zgodovini prodaj.</p>

<h2>Komunikacija s kupcem</h2>
<p>Topla, kratka in jasna sporočila bistveno gradijo zaupanje. Predloga za odgovor po prejetem naročilu:</p>
<blockquote><p>Pozdravljeni [ime kupca], hvala za naročilo v moji trgovini. Predračun ste prejeli po e-pošti. Po prejetem plačilu vam izdelek odpošljem najkasneje v 2 delovnih dneh. Lep pozdrav, [vaše ime]</p></blockquote>

<h2>Pogosta vprašanja in odgovori</h2>
<p><strong>1. Ali moram predračun pripraviti sam/a v Wordu?</strong><br/>Ne, sistem ga generira avtomatsko iz podatkov vaše trgovine in naročila kupca. PDF je takoj na voljo v zavihku <em>Poslani predračuni</em>.</p>
<p><strong>2. Zakaj kupec ne more zaključiti nakupa pri meni?</strong><br/>Verjetno v <a class="decorated-link" href="/trgovina">Moja trgovina</a> manjkajo obvezni podatki (naslov, mesto, IBAN). Dopolnite jih in poskusite znova.</p>
<p><strong>3. Kdaj izdelek odpošljem?</strong><br/>Po prejemu plačila. Plačilo običajno prispe v 1–2 delovnih dneh; sistem status posodobi samodejno.</p>
<p><strong>4. Ali sistem izda tudi račun?</strong><br/>Da, PDF računa je v <a class="decorated-link" href="/prodaja">Zgodovina prodaj</a>. Za davčne zavezance je račun obvezen po prejemu plačila.</p>
<p><strong>5. Kako kupcu pošljem številko sledenja paketa?</strong><br/>V <em>Zgodovini prodaj</em> pri naročilu odprete <em>Odpošlji</em>, izberete prevoznika in vnesete št. sledenja. Kupec obvestilo prejme po e-pošti.</p>
`,
  },

  "kako-povezati-trgovino-s-socialnimi-omrezji": {
    excerpt:
      "Kako v zavihku Moja trgovina povežete profile na socialnih omrežjih in iz vsake objave usmerite kupce na svojo trgovino na Unikatni Tržnici.",
    content: `
<p>Socialna omrežja so eden najmočnejših brezplačnih virov obiska vaše trgovine. Povezava med vašim Instagramom, Facebookom in trgovino na Unikatni Tržnici mora biti vidna in dosledna.</p>

<h2>1. Dodajte povezave v zavihku Moja trgovina</h2>
<p>V <a class="decorated-link" href="/trgovina">Moja trgovina → Družbena omrežja</a> najdete polja za:</p>
<ul>
<li>Facebook stran,</li>
<li>Instagram profil,</li>
<li>YouTube kanal,</li>
<li>Pinterest,</li>
<li>TikTok,</li>
<li>vaša spletna stran ali blog.</li>
</ul>
<p>Vnesite polni URL (npr. <em>https://www.instagram.com/vasprofil</em>). Ikone se po shranjevanju samodejno prikažejo na strani vaše trgovine in kupci vas tako najdejo tudi na drugih kanalih.</p>

<h2>2. Pripnite povezavo do trgovine v svoj bio</h2>
<ul>
<li><strong>Instagram bio:</strong> dodajte povezavo do svoje strani na Unikatni Tržnici (oblika <em>https://unikatnatrznica.si/prodajalci/vase-ime</em>). To je glavna pot kupcev s tega kanala.</li>
<li><strong>Facebook stran:</strong> v razdelku <em>O nas</em> in pri gumbu <em>Akcija</em> nastavite povezavo do trgovine.</li>
<li><strong>TikTok in YouTube:</strong> v opisu kanala vedno omenite, kje izdelke kupite.</li>
</ul>

<h2>3. Vsaka objava naj usmerja v trgovino</h2>
<p>Lepa fotografija sama po sebi ne prodaja. Vsaki objavi dodajte jasno povabilo, na primer: <em>„Ta unikatni nakit najdete v moji trgovini na Unikatni Tržnici — povezava v bio.”</em></p>

<h2>4. Uporabite Instagram Stories in Reels</h2>
<ul>
<li><strong>Stories:</strong> nalepka s povezavo neposredno do izdelka.</li>
<li><strong>Reels:</strong> prikaz procesa izdelave — najboljši način, da prikažete vrednost ročnega dela.</li>
</ul>
<p>Za podrobnejša priporočila preberite <a class="decorated-link" href="/clanek/instagram-in-spletna-trgovina">Instagram in spletna trgovina</a>, <a class="decorated-link" href="/clanek/facebook-in-spletna-trgovina">Facebook in spletna trgovina</a> ter <a class="decorated-link" href="/clanek/pinterest-za-ustvarjalce">Pinterest za prodajalce</a>.</p>

<h2>5. Bodite dosledni in pristni</h2>
<p>Bolje dve kakovostni objavi tedensko kot deset nepripravljenih. Pokažite delo v ozadju, navdih, materiale in kupce z vašimi izdelki — pristnost prodaja.</p>

<h2>Pogoste napake</h2>
<ul>
<li>Profil brez povezave do trgovine — kupec ne ve, kje kupiti.</li>
<li>Objave samo končnih izdelkov, brez konteksta in zgodbe.</li>
<li>Neredne objave in nato dolga tišina.</li>
<li>Ignoriranje komentarjev in zasebnih sporočil.</li>
</ul>

<h2>Pogosta vprašanja in odgovori</h2>
<p><strong>1. Kje natanko vnesem povezave do socialnih omrežij?</strong><br/>V nadzorni plošči v <a class="decorated-link" href="/trgovina">Moja trgovina</a> v sklopu <em>Družbena omrežja</em>. Vnesite polni URL profila.</p>
<p><strong>2. Ali se ikone takoj prikažejo na strani trgovine?</strong><br/>Da, takoj ko spremembe shranite.</p>
<p><strong>3. Katero omrežje je najpomembnejše za prodajalce?</strong><br/>Instagram in Pinterest sta najbolj vizualna in najprimernejša za unikatne izdelke. Facebook je še vedno koristen za starejšo publiko in skupine.</p>
<p><strong>4. Kako kupce s socialnih omrežij pripeljem na konkreten izdelek?</strong><br/>V Stories uporabite nalepko s povezavo, v opisih kanalov pa pripnite povezavo do svojega profila prodajalca na Unikatni Tržnici.</p>
<p><strong>5. Ali povezave vplivajo na SEO trgovine?</strong><br/>Posredno da — več kakovostnih obiskov iz družbenih omrežij in povezav na vašo trgovino pomaga prepoznavnosti. Več v <a class="decorated-link" href="/clanek/seo-osnove-za-ustvarjalce">SEO osnove za prodajalce</a>.</p>
`,
  },

  "pakiranje-in-posiljanje-izdelkov": {
    excerpt:
      "Trenutek, ko kupec odpre paket, je del nakupne izkušnje. Nasveti za varno pakiranje, estetiko, kontrolni seznam in vnos sledilne številke v Zgodovino prodaj.",
    content: `
<p>Trenutek, ko kupec odpre paket, je del nakupne izkušnje — in pogosto odločilen za to, ali vas bo priporočil naprej. Vlaganje v pakiranje se vedno povrne.</p>

<h2>1. Varnost najprej</h2>
<ul>
<li>Krhke izdelke (keramika, steklo, nakit) zavijte v mehurčasto folijo ali svilen papir v več plasteh.</li>
<li>Uporabite trden karton, ki ustreza velikosti izdelka — preveč prazen prostor pomeni premikanje.</li>
<li>Prazne kotičke zapolnite s papirjem ali lesno volno.</li>
</ul>

<h2>2. Estetika pakiranja</h2>
<ul>
<li>Enotna embalaža z vašo barvno paleto postane del blagovne znamke.</li>
<li>Naravni materiali (rjavi papir, jutni trak, pečat) so zelo cenjeni pri kupcih unikatnih izdelkov.</li>
<li>Dodajte ročno napisano kartico z zahvalo — drobna gesta, velik vtis.</li>
</ul>

<h2>3. Kaj priložiti</h2>
<ul>
<li><strong>Zahvalo:</strong> ročno napisanih 1–2 stavka, ne preveč formalno.</li>
<li><strong>Navodila za nego:</strong> kako čistiti, hraniti, podaljšati življenje izdelka.</li>
<li><strong>Vizitka ali kartica</strong> s povezavo na vašo trgovino na Unikatni Tržnici in Instagram — za ponovne nakupe in priporočila.</li>
<li><strong>Drobno presenečenje</strong> (semena, magnet, vzorček) — kupec o tem rad pove naprej.</li>
</ul>

<h2>4. Pošiljanje</h2>
<ul>
<li>Za manjše pošiljke je <em>Pošta Slovenije (paket M/L)</em> najugodnejša izbira.</li>
<li>Za dražje izdelke izberite priporočeno pošiljko s sledilno številko.</li>
<li>Vašo dostavno politiko opišite v <a class="decorated-link" href="/trgovina">Moja trgovina → Dostava</a> in v <em>Pravila dostave</em>, da kupec ve, kaj pričakovati.</li>
</ul>

<h2>5. Vnos sledilne številke v nadzorni plošči</h2>
<p>Ko paket oddate, v <a class="decorated-link" href="/prodaja">Zgodovina prodaj</a> pri pripadajočem naročilu kliknete <strong>Odpošlji</strong>, izberete <strong>prevoznika</strong> in vnesete <strong>številko sledenja</strong>. Po potrditvi <em>Potrdi odpošiljanje</em> kupec samodejno prejme e-pošto z informacijo. S tem ima vse na enem mestu — predračun, račun in podatke o pošiljki.</p>

<h2>6. Skrb po dostavi</h2>
<p>Dva ali tri dni po dostavi pošljite kratko sporočilo: <em>„Upam, da je [izdelek] varno prispel in da vam je všeč. Z veseljem slišim povratno mnenje.”</em> Iskreno zanimanje za kupčevo izkušnjo loči prodajalce, ki imajo posel, od tistih, ki imajo zveste občudovalce.</p>

<h2>Kontrolni seznam pred oddajo paketa</h2>
<ul>
<li>☐ Izdelek varno zavit.</li>
<li>☐ Naslov in povratni naslov pravilna in čitljiva.</li>
<li>☐ Priložena zahvala in vizitka.</li>
<li>☐ Navodila za nego (če relevantno).</li>
<li>☐ Fotografija zapakiranega izdelka za vašo evidenco.</li>
<li>☐ Po oddaji vnesena sledilna številka v <a class="decorated-link" href="/prodaja">Zgodovina prodaj</a>.</li>
</ul>

<h2>Pogosta vprašanja in odgovori</h2>
<p><strong>1. Kdo zaračuna in plača poštnino?</strong><br/>Priporočamo, da poštnino vključite v ceno izdelka. Konkretno pravilo opišete v <em>Moja trgovina → Dostava</em>. Več v <a class="decorated-link" href="/clanek/kako-oblikovati-cene-izdelkov">Kako oblikovati cene izdelkov</a>.</p>
<p><strong>2. Kje vnesem sledilno številko?</strong><br/>V <a class="decorated-link" href="/prodaja">Zgodovina prodaj</a> odprete naročilo, kliknete <em>Odpošlji</em>, izberete prevoznika in vnesete št. sledenja. Kupec dobi e-pošto avtomatsko.</p>
<p><strong>3. Kako hitro naj izdelek odpošljem po prejetem plačilu?</strong><br/>Priporočamo v 1–3 delovnih dneh. Pri izdelkih po naročilu dobavni rok jasno zapišite že v opis izdelka.</p>
<p><strong>4. Kakšno embalažo uporabiti za ekološke kupce?</strong><br/>Rjav reciklirani papir, kartonsko škatlo brez plastike, jutni trak in vodotopne nalepke. Ekološka embalaža je del zgodbe vašega izdelka.</p>
<p><strong>5. Kaj naj naredim, če se paket izgubi ali poškoduje?</strong><br/>S kupcem komunicirajte odprto in pomagajte pri reklamaciji pri prevozniku. Za priporočene pošiljke je sledenje obvezen prvi korak.</p>
`,
  },
  "poslovna-darila": {
    title: "Unikatna poslovna darila: ideje, cene in gravura",
    excerpt:
      "Unikatna poslovna darila slovenskih prodajalcev — cenovni razredi od 10 do 100 €, količinska naročila, gravura z logotipom in realni dobavni roki.",
    content: `
<p><strong>Unikatna poslovna darila</strong> so tiha vizitka podjetja. Namesto serijskih promocijskih izdelkov lahko partnerjem in zaposlenim podarite nekaj, kar je ročno izdelano v Sloveniji — keramiko, les, usnje, naravno kozmetiko ali darilne škatle po meri. Vse najdete med <a class="decorated-link" href="/kategorije/poslovna-darila">poslovnimi darili</a> na Unikatni Tržnici.</p>

<h2>Zakaj izbrati ročno izdelana poslovna darila</h2>
<ul>
<li><strong>Zapomljivost:</strong> unikat se ne izgubi med desetimi enakimi promocijskimi darili.</li>
<li><strong>Lokalna zgodba:</strong> vsak izdelek ima znanega avtorja in kraj nastanka — to je vsebina, ki jo lahko dodate voščilu.</li>
<li><strong>Trajnost:</strong> naravni materiali in majhne serije namesto plastike.</li>
<li><strong>Podpora slovenskim prodajalcem:</strong> vaš proračun ostane v lokalnem okolju.</li>
</ul>

<h2>Cenovni razredi</h2>
<ul>
<li><strong>Do 15 €:</strong> naravna mila, kopalne soli, dišeče sveče, voščilnice, manjši leseni obeski.</li>
<li><strong>15–35 €:</strong> ročno izdelane keramične skodelice, usnjeni etuiji, kvačkani dodatki, mazila in kreme.</li>
<li><strong>35–70 €:</strong> darilne škatle s tremi do petimi izdelki, keramični seti, večji leseni izdelki.</li>
<li><strong>Nad 70 €:</strong> personalizirani unikati, umetniška dela in sestavljeni paketi za ključne partnerje.</li>
</ul>
<p>Za sestavljene pakete si oglejte <a class="decorated-link" href="/kategorije/darilne-skatle">darilne škatle</a>, za manjše pozornosti pa <a class="decorated-link" href="/clanek/disece-svece-kot-darilo">dišeče sveče</a>.</p>

<h2>Količinska naročila</h2>
<p>Ročna izdelava pomeni omejene serije. Pri večjih količinah velja nekaj pravil:</p>
<ol>
<li><strong>Povprašajte vnaprej.</strong> Prek gumba <em>Povpraševanje</em> na strani izdelka prodajalcu sporočite želeno količino in rok.</li>
<li><strong>Računajte na odstopanja.</strong> Ročno izdelani kosi niso identični — to je prednost, ne napaka, in jo velja omeniti prejemnikom.</li>
<li><strong>Razdelite naročilo.</strong> Za 100 in več enot pogosto pomaga kombinacija dveh ali treh prodajalcev.</li>
<li><strong>Predračun.</strong> Prodajalci vam lahko pripravijo predračun za plačilo po podjetju.</li>
</ol>

<h2>Gravura in personalizacija</h2>
<p>Logotip ali ime prejemnika naredi darilo osebno. Najbolj primerni materiali za gravuro so les, usnje, keramika in kovina. Pri naročilu pripravite logotip v vektorski obliki (SVG, PDF ali EPS) ter navedite želeno velikost in mesto gravure. Več idej najdete v članku <a class="decorated-link" href="/clanek/personalizirana-darila-z-gravuro">Personalizirana darila z gravuro</a>, za besedilo voščila pa v <a class="decorated-link" href="/clanek/posvetila-za-poslovna-darila">Posvetila za poslovna darila</a>.</p>

<h2>Dobavni roki</h2>
<ul>
<li><strong>Izdelki na zalogi:</strong> praviloma 2–5 delovnih dni.</li>
<li><strong>Personalizacija ali gravura:</strong> dodatnih 5–10 delovnih dni.</li>
<li><strong>Večje serije (30+ kosov):</strong> 3–6 tednov, odvisno od tehnike izdelave.</li>
<li><strong>December:</strong> naročila oddajte do sredine novembra — takrat so prodajalci najbolj zasedeni.</li>
</ul>

<h2>Pogosta vprašanja o poslovnih darilih</h2>
<p><strong>1. Kakšna je primerna vrednost poslovnega darila?</strong><br/>Za zaposlene in stranke se najpogosteje giblje med 15 in 50 € na osebo, za ključne partnerje tudi več. Vrednost uskladite z internimi pravili obdarovanja.</p>
<p><strong>2. Ali lahko dobim račun na podjetje?</strong><br/>Da. Prodajalec vam pred plačilom pripravi predračun, po plačilu pa račun z vsemi podatki podjetja.</p>
<p><strong>3. Ali je mogoča dostava na več naslovov?</strong><br/>Da, dogovor je individualen — v povpraševanju navedite število naslovov in želeni rok dostave.</p>
<p><strong>4. Kako naročim gravuro logotipa?</strong><br/>V povpraševanju priložite logotip v vektorski obliki in navedite želeno mesto gravure. Prodajalec vam pošlje predogled pred izdelavo.</p>
<p><strong>5. Kdaj naročiti darila za december?</strong><br/>Najkasneje do sredine novembra, pri personalizaciji pa do konca oktobra.</p>

<p><em>Sorodno branje: <a class="decorated-link" href="/clanek/slovenski-spominki-za-poslovne-partnerje">Slovenski spominki za poslovne partnerje</a> in <a class="decorated-link" href="/clanek/eko-darila-trajnostni-izdelki-slovenija">Eko in trajnostna darila</a>.</em></p>
`,
  },
  // === Vsebinska poglobitev na podlagi podatkov Search Console (avgust 2026) ===
  // Ključna beseda "kvačkane torbice": 290 prikazov, poz. ~6 — obstoječi članek je bil pretanek.
  "unikatne-kvackane-torbice": {
    title: "Kvačkane torbice: vodnik po slovenskih unikatih, izbiri in negi",
    excerpt:
      "Kvačkane torbice slovenskih prodajalk — materiali, tehnike, velikosti, cene, kako prepoznati kakovostno kvačkano torbico in kako jo negovati, da leta ohrani obliko.",
    content: `
<p><strong>Kvačkane torbice</strong> so znova med najbolj iskanimi ročno izdelanimi modnimi dodatki — udobne, lahke in vsakič drugačne. Slovenske prodajalke jih kvačkajo iz bombažne, lanene, konopljine in reciklirane pređe, od poletnih košar iz rafije do elegantnih večernih torbic s podlogo. V tem vodniku pojasnimo, kako prepoznate kakovostno kvačkano torbico, koliko stane in kako jo negujete, da ohrani obliko.</p>

<h2>Zakaj izbrati kvačkano torbico</h2>
<ul>
<li><strong>Unikat:</strong> napetost roke pri kvačkanju ni serijska, zato ni dveh povsem enakih kosov.</li>
<li><strong>Trajnostna izbira:</strong> naravna ali reciklirana vlakna, brez industrijske proizvodnje.</li>
<li><strong>Lahka in prilagodljiva:</strong> vzorec se prilagodi vsebini, torbica pa ostane lahka.</li>
<li><strong>Vsestranska:</strong> od plažne košare do večerne torbice z verižico.</li>
<li><strong>Domača izdelava:</strong> vsak kos ima prodajalko z imenom in zgodbo.</li>
</ul>

<h2>Materiali in kaj pomenijo v praksi</h2>
<ol>
<li><strong>Bombažna pređa</strong> — mehka in pralna, najboljša za vsakodnevne torbice srednje velikosti.</li>
<li><strong>Rafija</strong> — poletna izbira s strukturo košare, drži obliko brez podloge.</li>
<li><strong>Konoplja in lan</strong> — trpežni vlakni za nakupovalne in plažne torbe, sčasoma se zmehčata.</li>
<li><strong>Reciklirani tekstil (t-shirt pređa)</strong> — debela in nosilna, primerna za košare in torbe za na plažo.</li>
<li><strong>Volna in merino</strong> — zimske torbice s toplim videzom; potrebujejo nežnejšo nego.</li>
</ol>

<h2>Vrste kvačkanih torbic po namenu</h2>
<h3>Vsakodnevna torbica čez ramo</h3>
<p>Srednja velikost (okrog 25 x 30 cm), gostejši vzorec, obvezna notranja podloga in zapiralo. Najbolj priljubljena izbira za v službo, na sprehod ali po opravkih.</p>
<h3>Plažna in nakupovalna košara</h3>
<p>Debela pređa ali rafija, širši ročaji in ojačano dno. Prenese težo, se hitro posuši in se po uporabi zloži.</p>
<h3>Večerna torbica</h3>
<p>Manjša, s tanjšo pređo, kovinsko verižico ali usnjenim paščkom in svileno podlogo. Pogosto v nevtralnih barvah ali s kovinsko nitjo.</p>
<h3>Otroška in mini torbica</h3>
<p>Lahka, brez trdih detajlov, s kratkim paščkom. Priljubljeno darilo za rojstni dan ali prvi šolski dan.</p>

<h2>Kako prepoznati kakovostno kvačkano torbico</h2>
<ul>
<li><strong>Gostota vzorca:</strong> pri gladkih vzorcih ne sme biti nezaželenih lukenj ali raztegnjenih zank.</li>
<li><strong>Ročaji:</strong> dvojno kvačkani, ojačani ali podloženi z usnjenim paščkom — nosijo največjo obremenitev.</li>
<li><strong>Notranja podloga:</strong> bombažna vreča z žepom drži obliko in prepreči, da bi drobni predmeti padli skozi vzorec.</li>
<li><strong>Ojačano dno:</strong> pri večjih modelih dvoslojno dno ali platneni vložek.</li>
<li><strong>Zaključki:</strong> vtaknjene niti, brez cufastih koncev in vozlov na vidnih mestih.</li>
<li><strong>Zapiralo:</strong> magnet, gumb ali zadrga — pomembno pri vsakodnevni rabi.</li>
</ul>

<h2>Koliko stane kvačkana torbica</h2>
<ul>
<li><strong>Mini in večerne torbice:</strong> 25–50 € (6–10 ur dela).</li>
<li><strong>Vsakodnevne torbice s podlogo:</strong> 50–90 € (12–20 ur dela).</li>
<li><strong>Velike košare in nakupovalne torbe:</strong> 60–120 € (15–25 ur dela).</li>
<li><strong>Torbice po meri z usnjenimi detajli:</strong> od 100 € naprej.</li>
</ul>
<p>Cena odraža ure ročnega dela in ceno pređe — najbolj poštena primerjava je cena na uro izdelave, ne cena na kos.</p>

<h2>Kako izbrati velikost in barvo</h2>
<ul>
<li><strong>Za vsak dan</strong> izberite naravno, ekru, terakoto, sivo ali črno — ujamejo se z vsem.</li>
<li><strong>Za poletje</strong> so priljubljene rafija v naravnem odtenku in barvni bloki.</li>
<li><strong>Za darilo</strong> izberite srednjo velikost s podlogo — najbolj univerzalna izbira.</li>
<li><strong>Preverite dolžino paščka:</strong> čez ramo 55–60 cm, čez telo 110–120 cm.</li>
</ul>

<h2>Nega in vzdrževanje</h2>
<ol>
<li>Perite <strong>ročno v mlačni vodi</strong> z blagim šamponom za volno; nikoli v stroju na visokih vrtljajih.</li>
<li>Ne ožemajte — torbico stisnite v brisačo in ročno oblikujte v končno obliko.</li>
<li>Sušite <strong>ploščato</strong>, nikoli obešeno, sicer se ročaji raztegnejo.</li>
<li>Ko je ne uporabljate, jo napolnite s papirjem ali mehko krpo, da drži obliko.</li>
<li>Madeže odstranjujte točkovno, takoj in brez drgnjenja.</li>
</ol>

<h2>Kvačkana torbica kot darilo</h2>
<p>Kvačkana torbica je odlično darilo za rojstni dan, materinski dan ali za slovo od sodelavke — osebna, uporabna in v vsakem primeru unikat. Če iščete še ideje, poglejte <a class="decorated-link" href="/clanek/darila-za-zenske">unikatna darila za ženske</a> ali <a class="decorated-link" href="/clanek/darilne-skatle">darilne škatle slovenskih prodajalcev</a>.</p>

<h2>Sorodno branje</h2>
<ul>
<li><a class="decorated-link" href="/clanek/unikatne-torbice">Unikatne torbice slovenskih prodajalcev</a></li>
<li><a class="decorated-link" href="/clanek/unikatne-torbice-iz-blaga">Unikatne torbice iz blaga</a></li>
<li><a class="decorated-link" href="/clanek/unikatne-usnjene-torbice">Unikatne usnjene torbice</a></li>
<li><a class="decorated-link" href="/clanek/kvackanje">Kvačkanje: tehnika in navdih</a></li>
</ul>

<h2>❓ Pogosta vprašanja (FAQ)</h2>
<p><strong>1. Ali kvačkana torbica zdrži vsakodnevno rabo?</strong><br/>Da, če je iz kakovostne pređe, ima ojačane ročaje in notranjo podlogo. Za težje vsebine izberite gostejši vzorec in dvoslojno dno.</p>
<p><strong>2. Koliko časa nastaja kvačkana torbica?</strong><br/>Manjša večerna 6–10 ur, vsakodnevna s podlogo 12–20 ur, velika košara 15–25 ur.</p>
<p><strong>3. Ali se kvačkane torbice raztegnejo?</strong><br/>Nekoliko se prilagodijo vsebini. Trdna notranja podloga in ojačano dno raztezanje občutno zmanjšata.</p>
<p><strong>4. Kako perem kvačkano torbico?</strong><br/>Ročno v mlačni vodi z blagim šamponom, brez ožemanja, sušenje ploščato v končni obliki.</p>
<p><strong>5. Kje kupim slovenske kvačkane torbice?</strong><br/>V kategoriji <a class="decorated-link" href="/kategorije/torbice">torbice</a> in <a class="decorated-link" href="/kategorije/moda">moda</a> na Unikatni tržnici, neposredno od prodajalk.</p>

<h2>Zaključek</h2>
<p>Dobra <strong>kvačkana torbica</strong> je kombinacija prave pređe, ojačanih ročajev in podloge, ki drži obliko. Če izberete kos slovenske prodajalke, dobite še zgodbo in možnost izdelave po meri — v barvi in velikosti, ki jo res potrebujete.</p>
`,
  },

  // Ključna beseda "unikatna darila za ženske": 83 prikazov, poz. ~7,6 — dopolnjeno in razširjeno.
  "darila-za-zenske": {
    title: "Unikatna darila za ženske: 15 idej slovenskih prodajalcev",
    excerpt:
      "Unikatna darila za ženske, ki jih ne dobite v trgovskem centru — 15 idej ročno izdelanih daril slovenskih prodajalcev, razvrščenih po priložnosti, značaju in proračunu.",
    content: `
<p>Najlepše darilo za žensko ni najdražje, ampak tisto, ob katerem prejemnica začuti, da ste jo <em>videli</em>. <strong>Unikatna darila za ženske</strong> to omogočajo: ročno izdelan kos slovenske prodajalke je izdelan v majhni seriji, pogosto ga je mogoče personalizirati, in nosi zgodbo, ki jo lahko poveste ob predaji.</p>

<h2>Kako izbrati darilo, ki bo zares zadelo</h2>
<ul>
<li><strong>Izhajajte iz njenega dne:</strong> kaj uporablja vsak dan — skodelico, torbico, kremo, nakit?</li>
<li><strong>Upoštevajte značaj:</strong> minimalistka, ljubiteljica narave, prodajalka, praktična organizatorka.</li>
<li><strong>Dodajte osebno noto:</strong> gravura imena, datum, začetnica, barva, ki jo nosi.</li>
<li><strong>Manj kosov, več pomena:</strong> en premišljen izdelek prekaša tri naključne.</li>
</ul>

<h2>15 idej za unikatna darila za ženske</h2>
<h3>1. Ročno izdelan nakit z gravuro</h3>
<p>Obesek z začetnico, prstan s teksturo ali uhani iz srebra — poglejte <a class="decorated-link" href="/clanek/unikatni-nakit-slovenskih-ustvarjalcev">unikatni nakit slovenskih prodajalcev</a>.</p>
<h3>2. Keramična skodelica po meri</h3>
<p>Ročno vlivana skodelica z motivom ali imenom naredi jutranjo kavo obred. Izbira v <a class="decorated-link" href="/clanek/unikatne-skodelice">unikatnih skodelicah</a>.</p>
<h3>3. Naravna kozmetika in nega</h3>
<p>Mila, mazila, kopalne soli in <a class="decorated-link" href="/kategorije/hidrolati">hidrolati</a> brez sintetičnih dodatkov — vedno uporabno darilo.</p>
<h3>4. Kvačkana ali šivana torbica</h3>
<p>Unikaten modni dodatek v barvi po izbiri; več v vodniku <a class="decorated-link" href="/clanek/unikatne-kvackane-torbice">kvačkane torbice</a>.</p>
<h3>5. Dišeča sveča iz sojinega voska</h3>
<p>Nežna, brez agresivnih dišav — <a class="decorated-link" href="/clanek/sojine-svece-slovenskih-ustvarjalcev">sojine sveče slovenskih prodajalcev</a>.</p>
<h3>6. Šal ali kapa iz merino volne</h3>
<p>Lahka, topla in brez praskanja; nevtralne barve so najbolj hvaležne.</p>
<h3>7. Makrame dekoracija</h3>
<p>Obesek za rastline ali stenska dekoracija — poglejte <a class="decorated-link" href="/clanek/makrame-dekoracija-za-dom">makrame dekoracijo za dom</a>.</p>
<h3>8. Ročno vezana beležka ali dnevnik</h3>
<p>Za ženske, ki pišejo, načrtujejo ali rišejo — z usnjeno ali platneno vezavo.</p>
<h3>9. Keramična vaza ali skleda</h3>
<p>Kos, ki spremeni mizo ali polico; <a class="decorated-link" href="/clanek/keramicne-vaze-slovenskih-ustvarjalcev">keramične vaze slovenskih prodajalcev</a>.</p>
<h3>10. Darilna škatla po meri</h3>
<p>Sestavite tri manjše kose — sveča, milo, skodelica — v <a class="decorated-link" href="/clanek/darilne-skatle">darilni škatli</a> z ročno pisanim posvetilom.</p>
<h3>11. Slika, ilustracija ali akvarel</h3>
<p>Portret hiše, ilustracija najljubšega kraja ali abstraktna kompozicija v njeni barvni paleti.</p>
<h3>12. Lanena kuhinjska ali kopalniška tekstilija</h3>
<p>Lanene brisače, prti in predpasniki — trajnostna izbira, ki z uporabo postaja lepša.</p>
<h3>13. Zeliščni čaji in med domačih pridelovalcev</h3>
<p>Darilo, ki se porabi in ne zaseda prostora; lepo deluje v kombinaciji s keramično skodelico.</p>
<h3>14. Personaliziran obesek ali medaljon s fotografijo</h3>
<p>Za mame in babice — z imeni otrok ali vnukov. Več v <a class="decorated-link" href="/clanek/unikatna-darila-za-babico">unikatnih darilih za babico</a>.</p>
<h3>15. Doživetje z ročno izdelanim spominkom</h3>
<p>Delavnica kvačkanja, keramike ali izdelave mila, ki jo pospremite z majhnim unikatnim darilom.</p>

<h2>Darilo glede na priložnost</h2>
<p><strong>Rojstni dan:</strong> nakit, torbica, keramika — glejte <a class="decorated-link" href="/clanek/uporabno-darilo-za-rojstni-dan">uporabna darila za rojstni dan</a>.<br/>
<strong>Materinski dan:</strong> obesek z imeni otrok, naravna kozmetika, darilna škatla.<br/>
<strong>Obletnica ali valentinovo:</strong> personaliziran nakit ali skupna fotografija v ročno izdelanem okvirju.<br/>
<strong>Slovo od sodelavke:</strong> manjša pozornost do 30 € — <a class="decorated-link" href="/clanek/darila-za-sodelavko-ob-slovesu">darila za sodelavko ob slovesu</a>.<br/>
<strong>Za novo mamico:</strong> lanena tekstilija, blaga nega, topla odeja.</p>

<h2>Ideje po proračunu</h2>
<h3>Do 20 €</h3>
<ul><li>Naravno milo ali kopalne soli.</li><li>Keramični lonček.</li><li>Ročno izdelana voščilnica z verzom.</li></ul>
<h3>20–50 €</h3>
<ul><li>Dišeča sveča iz sojinega voska.</li><li>Uhani ali obesek iz srebra.</li><li>Lanena kuhinjska tekstilija.</li></ul>
<h3>50–120 €</h3>
<ul><li>Kvačkana torbica s podlogo.</li><li>Keramična vaza ali komplet skodelic.</li><li>Merino šal ali pled.</li></ul>
<h3>Nad 120 €</h3>
<ul><li>Nakit po meri z gravuro.</li><li>Slika ali ilustracija po naročilu.</li><li>Sestavljena darilna škatla iz več ročno izdelanih kosov.</li></ul>

<h2>Kdaj naročiti</h2>
<ul>
<li><strong>Izdelki na zalogi:</strong> 2–5 delovnih dni do dostave.</li>
<li><strong>Z gravuro ali personalizacijo:</strong> 7–14 dni.</li>
<li><strong>Po meri (nakit, slika, tekstil):</strong> 3–6 tednov.</li>
<li><strong>December in materinski dan:</strong> naročite vsaj tri tedne prej — takrat so prodajalke najbolj zasedene.</li>
</ul>

<h2>Sorodno branje</h2>
<ul>
<li><a class="decorated-link" href="/clanek/najboljsa-darila-zanjo">Najboljša darila zanjo</a></li>
<li><a class="decorated-link" href="/clanek/unikatno-darilo-za-sestro">Unikatno darilo za sestro</a></li>
<li><a class="decorated-link" href="/clanek/unikatna-darila-za-materinski-dan">Unikatna darila za materinski dan</a></li>
<li><a class="decorated-link" href="/clanek/ideje-za-personalizirana-darila">Ideje za personalizirana darila</a></li>
</ul>

<h2>❓ Pogosta vprašanja (FAQ)</h2>
<p><strong>1. Kakšno je dobro unikatno darilo za žensko, ki ima vse?</strong><br/>Personalizirano ali porabno: nakit z gravuro imena, keramika po meri, naravna kozmetika ali doživetje z majhnim spominkom.</p>
<p><strong>2. Koliko naj porabim za darilo?</strong><br/>Za sodelavko 15–30 €, za prijateljico 30–60 €, za partnerico ali mamo najpogosteje 60–150 €. Osebna nota šteje več od zneska.</p>
<p><strong>3. Ali lahko darilo personaliziram?</strong><br/>Večina slovenskih prodajalk ponuja gravuro, vezenje ali izbiro barve. Želje sporočite ob naročilu in dodajte teden časa.</p>
<p><strong>4. Kaj podariti, če ne poznam njenega okusa?</strong><br/>Izberite porabno darilo (sveča, čaj, milo) ali darilno škatlo z več manjšimi kosi v nevtralnih barvah.</p>
<p><strong>5. Zakaj izbrati ročno izdelano darilo namesto trgovinskega?</strong><br/>Ker je izdelano v majhni seriji, pogosto po meri, in ker denar ostane pri slovenskem prodajalcu.</p>

<h2>Zaključek</h2>
<p><strong>Unikatna darila za ženske</strong> delujejo, ker povedo zgodbo — o prodajalki, ki jih je izdelala, in o vas, ki ste jih izbrali. Brskajte po kategorijah <a class="decorated-link" href="/kategorije/nakit">nakit</a>, <a class="decorated-link" href="/kategorije/naravna-kozmetika">naravna kozmetika</a> in <a class="decorated-link" href="/kategorije/moda">moda</a>.</p>
`,
  },

  // Ključna beseda "ročno izdelan nakit": kategorija /kategorije/nakit ima 477 prikazov na poz. ~24.
  "unikatni-nakit-slovenskih-ustvarjalcev": {
    title: "Ročno izdelan nakit slovenskih prodajalcev: vodnik po izbiri",
    excerpt:
      "Ročno izdelan nakit slovenskih prodajalcev — materiali, tehnike, cene, kako prepoznati kakovosten kos, kako izbrati velikost in kako nakit negovati, da ohrani sijaj.",
    content: `
<p><strong>Ročno izdelan nakit</strong> je najbolj oseben modni dodatek: nastaja v majhnih delavnicah, pogosto po meri, in ga je skoraj vedno mogoče prilagoditi — velikost, dolžina verižice, gravura. V tem vodniku pojasnimo, po čem prepoznate kakovosten kos slovenskega prodajalca, koliko stane in kako ga negujete.</p>

<h2>Zakaj ročno izdelan nakit</h2>
<ul>
<li><strong>Unikatnost:</strong> vsak kos nastane posamično, tudi znotraj iste serije se detajli razlikujejo.</li>
<li><strong>Prilagodljivost:</strong> velikost prstana, dolžina verižice, izbira kamna ali gravura.</li>
<li><strong>Znano poreklo:</strong> veste, kdo je kos izdelal in iz česa.</li>
<li><strong>Popravljivost:</strong> prodajalec kos praviloma tudi popravi ali predela.</li>
</ul>

<h2>Materiali in kaj pomenijo v nošenju</h2>
<ol>
<li><strong>Srebro 925:</strong> najpogostejša izbira, trpežno in cenovno dostopno; sčasoma potemni, a se zlahka očisti. Več v vodniku <a class="decorated-link" href="/clanek/rocno-izdelan-srebrni-nakit">ročno izdelan srebrni nakit</a>.</li>
<li><strong>Zlato in pozlata:</strong> za posebne priložnosti; pri pozlati preverite debelino sloja.</li>
<li><strong>Baker in medenina:</strong> topel odtenek, cenovno dostopno, občasno potrebuje poliranje.</li>
<li><strong>Steklo in smola:</strong> barvite, lahke kombinacije — glejte <a class="decorated-link" href="/clanek/steklen-nakit-slovenskih-ustvarjalcev">steklen nakit slovenskih prodajalcev</a>.</li>
<li><strong>Les in naravni materiali:</strong> zelo lahki uhani, primerni za občutljiva ušesa.</li>
<li><strong>Tekstil in kvačkanje:</strong> mehki, barviti kosi za vsak dan.</li>
</ol>

<h2>Kako prepoznati kakovosten ročno izdelan nakit</h2>
<ul>
<li><strong>Zaključki in spoji:</strong> gladki, brez ostrih robov in vidnih ostankov spajkanja.</li>
<li><strong>Zapiralo:</strong> trdno, enostavno za samostojno zapenjanje, brez zatikanja.</li>
<li><strong>Oznaka materiala:</strong> pri srebru žig 925, pri drugih materialih jasen opis v izdelku.</li>
<li><strong>Kljukice za uhane:</strong> pri občutljivih ušesih izberite srebrne ali kirurško jeklo.</li>
<li><strong>Enakomernost:</strong> pri paru uhanov naj bosta kosa usklajena po velikosti in teži.</li>
<li><strong>Opis izdelave:</strong> prodajalec navede tehniko, mere in težo kosa.</li>
</ul>

<h2>Koliko stane ročno izdelan nakit</h2>
<ul>
<li><strong>Uhani iz stekla, lesa ali tekstila:</strong> 15–35 €.</li>
<li><strong>Srebrni uhani ali obesek:</strong> 35–90 €.</li>
<li><strong>Srebrn prstan s teksturo ali kamnom:</strong> 60–150 €.</li>
<li><strong>Kos po meri z gravuro ali kamnom po izbiri:</strong> od 120 € naprej.</li>
</ul>

<h2>Kako izbrati pravo velikost in dolžino</h2>
<ul>
<li><strong>Prstan:</strong> obseg prsta izmerite zvečer, ko so prsti nekoliko večji; prodajalec vam pošlje merilni trak ali tabelo.</li>
<li><strong>Verižica:</strong> 40–45 cm sedi ob vratu, 50 cm pade pod ključnico, 60 cm nad prsni koš.</li>
<li><strong>Zapestnica:</strong> obseg zapestja plus 1,5–2 cm za udobje.</li>
<li><strong>Uhani:</strong> za vsak dan izberite lahke kose do 5 g na uhan.</li>
</ul>

<h2>Nega, ki ohrani sijaj</h2>
<ol>
<li>Nakit snemite pred tuširanjem, plavanjem, športom in spanjem.</li>
<li>Parfum, kremo in lak za lase nanesite <strong>pred</strong> nakitom, ne po njem.</li>
<li>Srebro hranite v zaprti vrečki; potemnelo očistite s krpico za srebro, ne z abrazivnimi sredstvi.</li>
<li>Steklo in smolo brišite z mehko suho krpo, brez alkohola.</li>
<li>Kose hranite ločeno, da se ne praskajo.</li>
</ol>

<h2>Nakit kot darilo</h2>
<p>Nakit je eno najbolj hvaležnih daril — zavzame malo prostora, a ima veliko pomena. Če kupujete darilo, izberite nevtralen kos v srebru ali z začetnico; praktične nasvete najdete v vodniku <a class="decorated-link" href="/clanek/kako-izbrati-unikatni-nakit-za-darilo">kako izbrati unikatni nakit za darilo</a>, več idej pa v <a class="decorated-link" href="/clanek/darila-za-zenske">unikatnih darilih za ženske</a>.</p>

<h2>Sorodno branje</h2>
<ul>
<li><a class="decorated-link" href="/clanek/unikatni-uhani">Unikatni uhani slovenskih prodajalcev</a></li>
<li><a class="decorated-link" href="/clanek/rocno-izdelan-srebrni-nakit">Ročno izdelan srebrni nakit</a></li>
<li><a class="decorated-link" href="/clanek/steklen-nakit-slovenskih-ustvarjalcev">Steklen nakit slovenskih prodajalcev</a></li>
<li><a class="decorated-link" href="/clanek/unikatni-nakit-ki-pripoveduje-zgodbo">Unikatni nakit, ki pripoveduje zgodbo</a></li>
</ul>

<h2>❓ Pogosta vprašanja (FAQ)</h2>
<p><strong>1. Kaj pomeni oznaka 925 na srebrnem nakitu?</strong><br/>Da je izdelek iz srebra čistine 92,5 %, kar je standard za nakit; preostanek so zlitine, ki dodajo trdnost.</p>
<p><strong>2. Zakaj srebro potemni in kako ga očistim?</strong><br/>Potemni zaradi reakcije z zrakom in kožnimi izločki. Očistite ga s krpico za srebro ali blago kopeljo iz mlačne vode in nekaj kapljic detergenta.</p>
<p><strong>3. Ali je ročno izdelan nakit primeren za občutljiva ušesa?</strong><br/>Da, če izberete srebrne kljukice ali kirurško jeklo in se izognete niklju.</p>
<p><strong>4. Ali lahko naročim nakit po meri?</strong><br/>Da. Večina slovenskih prodajalcev izdela kos po meri — sporočite velikost, material in želeno gravuro; izdelava traja običajno 2–4 tedne.</p>
<p><strong>5. Kje kupim ročno izdelan slovenski nakit?</strong><br/>V kategoriji <a class="decorated-link" href="/kategorije/nakit">nakit</a> na Unikatni tržnici, kjer prodajajo prodajalci neposredno.</p>

<h2>Zaključek</h2>
<p><strong>Ročno izdelan nakit</strong> izberete enkrat, nosite pa leta. Preverite material, zaključke in zapiralo, izmerite velikost in vprašajte prodajalca za možnost personalizacije — dobili boste kos, ki ga ni v nobeni verižni trgovini.</p>
`,
  },

  // Dopolnitev za iskalni namen "aranžiranje denarja za darilo" (CTR 40 %, nizka količina prikazov).
  "kako-zaviti-denar-za-porocno-darilo": {
    title: "Kako zaviti denar za darilo: 12 idej za aranžiranje denarja",
    excerpt:
      "Aranžiranje denarja za darilo — 12 kreativnih idej, kako zaviti denar za poroko, rojstni dan ali obletnico, s slovenskim ročnim delom in nasveti za posvetilo.",
    content: `
<p>Denar je najbolj praktično darilo — a lahko izgleda tudi osebno in domiselno. <strong>Aranžiranje denarja</strong> pomeni, da bankovcem dodate obliko, embalažo in posvetilo, zaradi katerih darilo ni več le znesek. V tem vodniku pokažemo 12 idej, <strong>kako zaviti denar za poročno darilo</strong>, rojstni dan, obletnico ali diplomo.</p>

<h2>Zakaj se splača aranžirati denar</h2>
<p>Denar sam po sebi je funkcionalen, a brezoseben. Ko ga zavijete premišljeno, darilo dobi vrednost pozornosti — dokaz, da ste vložili čas in idejo, ne le vsoto. Kombinacija manjšega ročno izdelanega darila in denarja je med prejemniki najbolje sprejeta oblika.</p>

<h2>12 idej za aranžiranje denarja</h2>
<ol>
<li><strong>Origami srce ali metuljček</strong> iz bankovca, vstavljen v lesen okvir ali voščilnico.</li>
<li><strong>Denarno drevo</strong> — bankovci, pripeti na vejo v keramični vazi.</li>
<li><strong>Denarni šopek</strong> — bankovci, zviti kot cvetni popki, med suho sivko in gipsonko.</li>
<li><strong>Voščilnica z žepki</strong> — <a class="decorated-link" href="/clanek/voscilnice">ročno izdelana voščilnica</a> z izrezanimi žepki za bankovce.</li>
<li><strong>Lesena skrinjica s ključkom</strong>, v kateri je denar zložen med suhe cvetove.</li>
<li><strong>Steklen kozarec z etiketo</strong> ("za prvi skupni izlet") in zvitki bankovcev z vrvico.</li>
<li><strong>Škatla presenečenja</strong> — <a class="decorated-link" href="/clanek/darilne-skatle">darilna škatla</a> z bankovci, svečo in posvetilom.</li>
<li><strong>Balončni šopek</strong> — bankovci v prozornih balonih s helijem.</li>
<li><strong>Usnjen ovoj ali etui</strong> slovenskega usnjarja, ki po dogodku ostane v uporabi.</li>
<li><strong>Denarna harmonika</strong> — bankovci, zlepljeni v pas, ki se ob odpiranju voščilnice razpre.</li>
<li><strong>Potovalni zemljevid</strong> z označenim krajem in denarjem v zadnjem žepu okvirja.</li>
<li><strong>Kuverta iz lanenega blaga</strong> z izvezenim imenom ali datumom.</li>
</ol>

<h2>Katera ideja za katero priložnost</h2>
<ul>
<li><strong>Poroka:</strong> denarno drevo, škatla presenečenja, lesena skrinjica, kuverta iz lanu.</li>
<li><strong>Rojstni dan:</strong> origami, denarna harmonika, kozarec z etiketo.</li>
<li><strong>Diploma:</strong> zemljevid s ciljem, usnjen etui, šopek iz bankovcev.</li>
<li><strong>Obletnica:</strong> okvir z origami srcem in fotografijo.</li>
<li><strong>Krst ali rojstvo:</strong> lesena skrinjica za varčevanje z gravuro imena in datuma.</li>
</ul>

<h2>Materiali, ki jih potrebujete</h2>
<ul>
<li>Kraft papir ali lanen prtiček.</li>
<li>Naravna vrvica ali satenast trak.</li>
<li>Suhi cvetovi — sivka, gipsonka, vrtnice.</li>
<li>Ročno izdelana <a class="decorated-link" href="/clanek/rocno-izdelane-voscilnice-za-poroko">poročna voščilnica</a>.</li>
<li>Lesena škatla, okvir ali keramična vaza slovenskega prodajalca.</li>
<li>Obojestranski lepilni trak in tanke sponke (brez luknjanja bankovcev).</li>
</ul>

<h2>Pravila, ki jih velja upoštevati</h2>
<ul>
<li><strong>Bankovcev ne lepite, luknjajte ali pišite po njih</strong> — poškodovanih bankovcev trgovine ne sprejemajo.</li>
<li>Uporabite <strong>mehke gube</strong> in obojestranski trak, ki se odstrani brez sledi.</li>
<li>Pri origamiju izberite <strong>novejše, ravne bankovce</strong> — lepše držijo obliko.</li>
<li><strong>Znesek naj bo diskretno skrit</strong>, ne razstavljen na ogled gostom.</li>
<li>Pri večjih zneskih raje uporabite <strong>zaprto škatlo ali kuverto</strong> kot odprt aranžma.</li>
</ul>

<h2>Koliko denarja podariti</h2>
<p>Priporočila po priložnosti in podrobnejšo razlago najdete v vodniku <a class="decorated-link" href="/clanek/porocno-darilo-v-denarju">poročno darilo v denarju: koliko dati</a>. Pravilo, ki drži v večini primerov: znesek prilagodite bližini odnosa in svojim zmožnostim, obliko darila pa priložnosti.</p>

<h2>Posvetilo, ki zaokroži darilo</h2>
<p>Aranžma brez besed je le embalaža. Dodajte kaligrafsko ali ročno napisano posvetilo — nekaj vrstic o tem, česa si za prejemnika želite. Za navdih preberite <a class="decorated-link" href="/clanek/darila-in-verzi-za-poroko">darila in verze za poroko</a> ali <a class="decorated-link" href="/clanek/unikatni-verzi-za-rojstni-dan">unikatne verze za rojstni dan</a>.</p>

<h2>Sorodno branje</h2>
<ul>
<li><a class="decorated-link" href="/clanek/porocno-darilo-v-denarju">Poročno darilo v denarju: koliko dati</a></li>
<li><a class="decorated-link" href="/clanek/unikatna-porocna-darila">Unikatna poročna darila</a></li>
<li><a class="decorated-link" href="/clanek/darilne-skatle">Darilne škatle slovenskih prodajalcev</a></li>
</ul>

<h2>❓ Pogosta vprašanja (FAQ)</h2>
<p><strong>1. Kaj pomeni aranžiranje denarja za darilo?</strong><br/>Da bankovcem dodate obliko in embalažo — origami, šopek, drevo, škatlo ali okvir — skupaj s posvetilom, da darilo deluje osebno.</p>
<p><strong>2. Ali je denar sprejemljivo darilo?</strong><br/>Da. Mladoporočenca ga najpogosteje uporabita za potovanje ali dom. Če je zavito osebno, izgubi občutek praznega darila.</p>
<p><strong>3. Kako zavarujem bankovce v origami darilu?</strong><br/>Uporabite mehke gube in ne lepite bankovcev; prejemnik jih po dogodku brez težav zravna in uporabi.</p>
<p><strong>4. Ali lahko denar priložim k drugemu darilu?</strong><br/>Da — kombinacija manjše ročno izdelane pozornosti in denarja je med prejemniki najbolj cenjena.</p>
<p><strong>5. Koliko časa vzame priprava?</strong><br/>Od 15 minut (kuverta z voščilom) do 2 ur (denarno drevo ali škatla presenečenja).</p>

<h2>Zaključek</h2>
<p>Dobro <strong>aranžiran denar</strong> združi praktičnost in pozornost. Izberite obliko, ki ustreza priložnosti, dodajte ročno izdelan detajl slovenskega prodajalca in kratko posvetilo — in denarno darilo bo ostalo v spominu.</p>
`,
  },
};
